import { useMemo, type FC, type ReactNode } from 'preact/compat';

import { Icon16CheckOutline, Icon16ClockOutline } from '@vkontakte/icons';
import { Caption, Headline, Subhead } from '@vkontakte/vkui';
import { clsx } from 'clsx';

import { useWidget } from '../../context/WidgetContext';
import type { Message } from '../../types/messages';
import { MessageSendStatus } from '../../types/messages';
import { WebsocketMessageType } from '../../types/ws-events';
import { getDateTime, sanitizeHtml } from '../../utils';

import { FilesList } from '../FilesList';
import { MessageContainer } from '../MessageContainer';
import { TextWithLink } from '../TextWithLink';

import styles from './styles.module.css';

type Props = Message & {
  messageFooter?: ReactNode;
};

export const MessageBubble: FC<Props> = (props) => {
  const { localeMessages } = useWidget();
  const { authorName, messageType, messageFooter, id } = props;

  const date = 'createDate' in props && props.createDate;
  const status = 'status' in props && props.status;
  const text = 'message' in props && props.message;
  const messageHtml = 'messageHtml' in props && props.messageHtml;
  const files = 'attachments' in props && props.attachments ? props.attachments : undefined;

  const isOutgoing = messageType === WebsocketMessageType.In;
  const isIncoming = messageType !== WebsocketMessageType.In;
  const showStatusInfo = status && status === MessageSendStatus.NotSent;

  const messageClass = clsx(styles.message, {
    [styles.outgoing]: isOutgoing,
    [styles.incoming]: isIncoming,
  });

  // Статус отправки сообщения
  const messageStatus =
    isOutgoing && status
      ? {
          [MessageSendStatus.Sent]: <Icon16CheckOutline className={styles.sentMessageIcon} />,
          [MessageSendStatus.NotSent]: <Icon16ClockOutline className={styles.notSentMessageIcon} />,
        }[status]
      : null;

  // Подробная информация по статусу отправки
  const statusInfo = useMemo(() => {
    if (status && status === MessageSendStatus.NotSent) {
      return <Subhead className={styles.error}>{localeMessages.chat.messageNotSent}</Subhead>;
    }

    return null;
  }, [status, localeMessages]);

  // Показываем HTML, если передан messageHtml (в т.ч. из messageJson.titleHtml для JSON_CHATBOT_REQUEST)
  const hasHtml = !!messageHtml;
  const sanitizedHtml = hasHtml ? sanitizeHtml(messageHtml) : null;

  return (
    <MessageContainer type={messageType} dataId={String(id)}>
      <div className={messageClass}>
        {authorName && (
          <Headline level="2" className={styles.author}>
            {isOutgoing ? localeMessages.chat.you : authorName}
          </Headline>
        )}
        <div className={styles.content}>
          {hasHtml && sanitizedHtml ? (
            <div className={styles.textHtml} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
          ) : (
            <TextWithLink className={styles.text} text={text || ''} />
          )}
          {date ? (
            <div className={styles.messageMeta}>
              <time dateTime={getDateTime(date)} className={styles.time}>
                <Caption level="2">{getDateTime(date)}</Caption>
              </time>
              {messageStatus}
            </div>
          ) : null}
        </div>

        <FilesList className={styles.files} itemClassName={styles.fileItem} files={files} />

        {messageFooter}
      </div>
      {showStatusInfo && <div className={styles.statusInfo}>{statusInfo}</div>}
    </MessageContainer>
  );
};
